import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'
import { Tooltip } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { useSubscriptionViewModel } from './subscription-view-model'

const primaryBtnClass = 'inline-flex h-11 items-center gap-2 self-start rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105'
const outlineBtnClass = 'inline-flex h-10 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover'

const SubscriptionView = () => {
    const model = useSubscriptionViewModel()

    return (
        <KarteraAdminTemplate loading={false} title="Mi plan">
            <div className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">Cargando tu suscripción…</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title="Mi plan"
                    description="Consulta tu suscripción, su vigencia y lo que incluye tu plan."
                    backTo={model.backTo}
                    backLabel="Configuración"
                    actions={model.portalUrl && (
                        <a href={model.portalUrl} className={outlineBtnClass}>
                            <OpenInNewOutlinedIcon style={{ fontSize: 18 }} />
                            Administrar suscripción
                        </a>
                    )}
                />

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                {!model.loading && !model.error && !model.hasSubscription && (
                    <section className="k-card items-start">
                        <h2 className="k-card-title">Suscripción</h2>
                        <p className="m-0 text-sm text-k-ink-2">No tienes una suscripción activa. Contrata un plan para seguir operando tu cartera.</p>
                        <Link to={model.plansLocation.pathname} state={model.plansLocation.state} className={primaryBtnClass}>
                            <CheckIcon />
                            Contratar un plan
                        </Link>
                    </section>
                )}

                {model.hasSubscription && (
                    <>
                        <section className="k-card">
                            <h2 className="k-card-title">Suscripción actual</h2>
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div className="flex min-w-0 items-center gap-3.5">
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-[linear-gradient(135deg,#3E6BE6,#8B3FC9_55%,#EC3B6C)] text-white">
                                        <WorkspacePremiumOutlinedIcon />
                                    </span>
                                    <div className="flex min-w-0 flex-col gap-1">
                                        <span className="truncate text-xl font-extrabold tracking-[-0.02em] text-k-ink">{model.planName}</span>
                                        {model.periodLabel && (
                                            <span className="self-start rounded-full bg-k-accent-soft px-2 py-0.5 text-xs font-bold text-k-accent-ink">{model.periodLabel}</span>
                                        )}
                                    </div>
                                </div>
                                {model.price && (
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-[28px] font-extrabold tabular-nums tracking-[-0.03em] text-k-ink">{model.price}</span>
                                        <span className="text-sm text-k-muted">MXN {model.priceSuffix}</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap items-center gap-3.5 rounded-[14px] border border-k-line-soft bg-k-sunken px-4 py-3.5">
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-tone-teal text-tone-teal-ink">
                                    <EventOutlinedIcon style={{ fontSize: 20 }} />
                                </span>
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <span className="text-[13px] text-k-muted">{model.renewalTitle}</span>
                                    <span className="text-base font-bold text-k-ink">{model.renewalDate}</span>
                                </div>
                                {model.remaining && (
                                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${model.expired ? 'bg-k-danger-soft text-k-danger-ink' : model.isTrial ? 'bg-tone-orange text-tone-orange-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                                        {model.remaining}
                                    </span>
                                )}
                            </div>
                            {model.startDate && <p className="m-0 text-xs text-k-muted">Vigente desde el {model.startDate}.</p>}
                        </section>

                        {model.features.length > 0 && (
                            <section className="k-card">
                                <h2 className="k-card-title">Incluye tu plan</h2>
                                <ul className="m-0 flex list-none flex-col p-0">
                                    {model.features.map((feature) => (
                                        <li key={feature.id} className="flex min-h-11 items-center gap-3 border-b border-k-line-soft py-2 last:border-b-0">
                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-k-ok-soft text-k-ok">
                                                <CheckIcon size={14} />
                                            </span>
                                            <span className="min-w-0 flex-1 text-sm text-k-ink-2">{feature.label}</span>
                                            {feature.value && <span className="text-sm font-bold tabular-nums text-k-ink">{feature.value}</span>}
                                            {feature.help && (
                                                <Tooltip title={feature.help}>
                                                    <HelpOutlineIcon className="text-k-muted-2" style={{ fontSize: 18 }} />
                                                </Tooltip>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {model.canHire && (
                            <section className="k-card items-start">
                                <h2 className="k-card-title">Contrata un plan</h2>
                                <p className="m-0 text-sm text-k-ink-2">Al terminar tu periodo de prueba necesitas un plan para seguir usando Kartera 365.</p>
                                <Link to={model.plansLocation.pathname} state={model.plansLocation.state} className={primaryBtnClass}>
                                    <CheckIcon />
                                    Contratar un plan
                                </Link>
                            </section>
                        )}

                        {model.portalUrl && (
                            <p className="m-0 text-center">
                                <a href={model.portalUrl} className="text-sm font-semibold text-k-danger hover:underline">Cancelar mi suscripción</a>
                            </p>
                        )}
                    </>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default SubscriptionView
